import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Star, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RatingModal({ activity, currentUser, onClose }) {
  const { t } = useI18n();
  const [participants, setParticipants] = useState([]);
  const [ratings, setRatings] = useState({}); // user_id -> stars
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const targets = (activity.participants || []).filter((u) => u !== currentUser?.user_id);
      const profiles = await Promise.all(targets.map((uid) => api.get(`/users/${uid}`).then((r) => r.data).catch(() => null)));
      setParticipants(profiles.filter(Boolean));
    })();
  }, [activity, currentUser]);

  const setStars = (uid, n) => setRatings((r) => ({ ...r, [uid]: n }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const entries = Object.entries(ratings).filter(([, n]) => n > 0);
      await Promise.all(entries.map(([uid, stars]) =>
        api.post("/ratings", { activity_id: activity.activity_id, rated_user_id: uid, stars })
      ));
      localStorage.setItem(`tf_rated_${activity.activity_id}`, "1");
      toast.success(t("rating.success"));
      onClose?.();
    } catch (e) {
      toast.error("Couldn't submit");
    } finally { setSubmitting(false); }
  };

  const skip = () => {
    localStorage.setItem(`tf_rated_${activity.activity_id}`, "1");
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="rating-modal">
      <div className="w-full max-w-lg bg-[#141414] border border-[#262626]">
        <div className="px-6 py-4 border-b border-[#262626] flex items-start justify-between">
          <div>
            <div className="label-overline text-[#007AFF]">{activity.title}</div>
            <h2 className="font-display text-3xl tracking-tight mt-1">{t("rating.title")}</h2>
            <p className="text-sm text-zinc-400 mt-2 max-w-sm">{t("rating.subtitle")}</p>
          </div>
          <button data-testid="rating-modal-close" onClick={skip} className="p-2 hover:bg-white/5 transition-colors">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="px-6 py-5 max-h-[55vh] overflow-y-auto space-y-3">
          {participants.length === 0 ? (
            <div className="text-sm text-zinc-500">No one to rate.</div>
          ) : participants.map((p) => (
            <div key={p.user_id} className="flex items-center justify-between gap-3 border border-[#262626] p-3" data-testid={`rating-row-${p.user_id}`}>
              <div className="flex items-center gap-3 min-w-0">
                {p.picture ? (
                  <img src={p.picture.startsWith("http") ? p.picture : `${process.env.REACT_APP_BACKEND_URL}${p.picture}`} alt="" className="w-9 h-9 object-cover" />
                ) : (
                  <div className="w-9 h-9 bg-[#1A1A1A] border border-[#262626] flex items-center justify-center text-xs">{(p.name || "?")[0]}</div>
                )}
                <div className="min-w-0">
                  <div className="text-sm truncate">{p.name}</div>
                  <div className="label-overline">{p.fitness_level || ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    data-testid={`rating-${p.user_id}-${n}`}
                    onClick={() => setStars(p.user_id, n)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${n} ${t("rating.stars")}`}
                  >
                    <Star size={22} weight={ratings[p.user_id] >= n ? "fill" : "duotone"} color={ratings[p.user_id] >= n ? "#FFCC00" : "#52525B"} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-[#262626] flex gap-2">
          <button data-testid="rating-skip" onClick={skip} className="kinetic-btn flex-1 border border-[#262626] hover:border-white/40 px-4 py-3 font-bold uppercase tracking-widest text-sm">
            {t("rating.skip")}
          </button>
          <button data-testid="rating-submit" onClick={submit} disabled={submitting || Object.values(ratings).every((v) => !v)} className="kinetic-btn flex-1 bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-50 text-white px-4 py-3 font-bold uppercase tracking-widest text-sm">
            {t("rating.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
